import { JSX } from 'react';
import styled, { css } from 'styled-components';
import StatusCard from './StatusCard';

const noRoundBorder = css`
  border-radius: 0;
`;

const Root = styled.div<{ disableRoundBorder: boolean }>`
  display: flex;
  background-color: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.palette.slateTint(80)};
  overflow-x: auto;
  border-radius: ${({ theme }) => theme.spacing / 2}px;
  ${({ disableRoundBorder }) => disableRoundBorder && noRoundBorder};
`;

export interface SummaryItemBase<T, TypeName extends string> {
  label: string;
  value: T;
  stick?: boolean;
  withDivider?: boolean;
  testId?: string;
  __typename: TypeName;
}

export type SummaryDateItem = SummaryItemBase<Date, 'date'>;
export type SummaryDurationItem = SummaryItemBase<number, 'duration'>;
export type SummaryPillStatusItem = SummaryItemBase<string, 'pill-status'>;
export type SummaryPreviewItem = SummaryItemBase<void, 'preview'>;

export type SummaryItem = SummaryDateItem | SummaryDurationItem | SummaryPillStatusItem | SummaryPreviewItem;

const SummaryItemStat = ({ stick = false, ...item }: SummaryItem): JSX.Element => {
  const stickAt = stick ? 'right' : undefined;

  switch (item.__typename) {
    case 'date':
      return <StatusCard data-testid={item.testId} type="date" {...{ stickAt, ...item }} />;
    case 'duration':
      return <StatusCard data-testid={item.testId} type="duration" {...{ stickAt, ...item }} />;
    case 'pill-status': {
      return <StatusCard data-testid={item.testId} type="pill-status" {...{ stickAt, ...item }} />;
    }
    case 'preview': {
      return <StatusCard data-testid={item.testId} type="preview" {...{ stickAt, ...item }} />;
    }
  }
};

interface SummaryCardsProps {
  items: SummaryItem[];
  disableRoundBorder?: boolean;
}

const SummaryCards = ({ items, disableRoundBorder = false }: SummaryCardsProps) => {
  return (
    <Root disableRoundBorder={disableRoundBorder}>
      {items.map((item, index) => (
        <SummaryItemStat key={item.label} withDivider={index !== 0} {...item} />
      ))}
    </Root>
  );
};

export default SummaryCards;
